// Tabela de bairros — linhas densas e escaneáveis, com edição inline do valor
// de entrega, coluna de % de utilização e ordenação pelos cabeçalhos
// (Bairro/Uso/Entrega — clique alterna asc/desc).
// Mobile: colunas CEP e USO somem; o CEP aparece abaixo do nome do bairro.
// Celular (25/10/2026): padding menor e nome quebra linha — a tabela cabe no card
// (antes 448 px num card de 342; editar/excluir ficavam fora da tela).

import { ArrowDown, ArrowUp, ArrowUpDown, Edit, MapPin, Plus, Trash2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { formatCep } from "../lib/cep";
import ValorEntregaCell from "./ValorEntregaCell";

function SortableTh({ campo, sort, onSort, align = "left", className = "", children }) {
  const ativo = sort?.campo === campo;
  const Icone = !ativo ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-2.5 sm:px-4 py-1 ${className}`}>
      <button
        type="button"
        onClick={() => onSort(campo)}
        title="Clique para ordenar"
        className={`inline-flex items-center gap-1 py-1.5 font-semibold uppercase tracking-wide transition-colors hover:text-foreground ${
          align === "right" ? "flex-row-reverse" : ""
        } ${ativo ? "text-foreground" : ""}`}
      >
        {children}
        <Icone className={`w-3 h-3 ${ativo ? "text-primary" : "opacity-40"}`} />
      </button>
    </th>
  );
}

function UsoCell({ usoPct, usoCount, windowDays }) {
  if (usoPct == null) {
    return <span className="text-muted-foreground/50 text-xs">sem dados</span>;
  }
  return (
    <div
      className="flex items-center gap-2"
      title={`${usoCount} pedido(s) nos últimos ${windowDays} dias`}
    >
      <div className="h-1.5 w-16 rounded-full bg-muted overflow-hidden shrink-0">
        <div
          className="h-full bg-primary rounded-full"
          style={{ width: `${Math.min(100, usoPct)}%` }}
        />
      </div>
      <span className="text-xs tabular-nums text-muted-foreground w-9">
        {usoPct < 1 && usoPct > 0 ? "<1" : Math.round(usoPct)}%
      </span>
    </div>
  );
}

export default function BairrosTable({
  bairros,
  windowDays,
  sort,
  onSort,
  onNovo,
  onEditar,
  onDeletar,
  onSalvarValor,
}) {
  if (bairros.length === 0) {
    return (
      <div className="text-center py-16 bg-muted/30 rounded-2xl border-2 border-dashed">
        <MapPin className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
        <h3 className="text-lg font-semibold mb-2">Nenhum bairro cadastrado</h3>
        <p className="text-muted-foreground mb-4">
          Cadastre bairros para definir valores de entrega
        </p>
        <Button onClick={onNovo}>
          <Plus className="w-4 h-4 mr-2" />
          Cadastrar primeiro bairro
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-xs text-muted-foreground">
              <SortableTh campo="nome" sort={sort} onSort={onSort} className="text-left">
                Bairro
              </SortableTh>
              <th className="text-left font-semibold uppercase tracking-wide px-2.5 sm:px-4 py-2.5 hidden sm:table-cell">
                CEP
              </th>
              <SortableTh
                campo="uso"
                sort={sort}
                onSort={onSort}
                className="text-left hidden md:table-cell"
              >
                Uso ({windowDays}d)
              </SortableTh>
              <SortableTh campo="valor" sort={sort} onSort={onSort} align="right" className="text-right">
                Entrega
              </SortableTh>
              <th className="w-24 px-2.5 sm:px-4 py-2.5">
                <span className="sr-only">Ações</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {bairros.map((b) => (
              <tr
                key={b.id}
                className="border-b last:border-b-0 hover:bg-muted/40 transition-colors"
              >
                <td className="px-2.5 sm:px-4 py-2.5">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <MapPin className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium break-words">{b.nome}</p>
                      {b.cep && (
                        <p className="text-xs text-muted-foreground font-mono sm:hidden">
                          {formatCep(b.cep)}
                        </p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-2.5 sm:px-4 py-2.5 hidden sm:table-cell">
                  {b.cep ? (
                    <span className="font-mono text-muted-foreground">{formatCep(b.cep)}</span>
                  ) : (
                    <span className="text-muted-foreground/50">—</span>
                  )}
                </td>
                <td className="px-2.5 sm:px-4 py-2.5 hidden md:table-cell">
                  <UsoCell usoPct={b.usoPct} usoCount={b.usoCount} windowDays={windowDays} />
                </td>
                <td className="px-2.5 sm:px-4 py-2.5 text-right">
                  <ValorEntregaCell bairro={b} onSalvar={onSalvarValor} />
                </td>
                <td className="px-2.5 sm:px-4 py-2.5">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      title="Editar bairro (nome, CEP, valor)"
                      onClick={() => onEditar(b)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      title="Remover bairro"
                      onClick={() => onDeletar(b)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
